import React, {useState, useEffect, useCallback} from 'react';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Button as MuiButton,
} from '@material-ui/core';
import formatDate from 'date-fns/format';
import differenceInDays from 'date-fns/differenceInDays';

import {
    EmptyText,
    ErrorAlert,
    Spinner,
} from '../utils/commonStyledComponents';
import {fetchVouchers, fetchVoucherNotice, generateVoucherFromPoints, generateVoucherFromCluster} from '../utils/api';
import {useUser} from '../utils/UserSession';
import {useAlert} from '../utils/AlertProvider';

// Rebranding 2026 · Vouchers allineato a vouchers-ivory.html
// - notice "Come funzionano i tuoi voucher" con gc-notice
// - band "I tuoi voucher · N voucher"
// - tabella MUI styled da skin.css (no styled-components custom)
// - pill stato Riscattato + pill warning "In scadenza" (<30 giorni)
// - codice voucher in font monospace

const Vouchers = () => {
    const [vouchers, setVouchers] = useState();
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [noticeHtml, setNoticeHtml] = useState('');
    const [loadingPoints, setLoadingPoints] = useState(false);
    const [loadingCluster, setLoadingCluster] = useState(false);
    const {user} = useUser();
    const showAlert = useAlert();

    const refresh = useCallback(async () => {
        try {
            const data = await fetchVouchers();
            setVouchers(data);
        } catch (e) {
            setShowErrorMessage(true);
        }
    }, []);

    const loadNotice = useCallback(async () => {
        try {
            const data = await fetchVoucherNotice();
            setNoticeHtml(data?.html || '');
        } catch (e) {
            setNoticeHtml('');
        }
    }, []);

    useEffect(() => {
        refresh();
        loadNotice();
    }, [refresh, loadNotice]);

    const handleSuccess = (message) => {
        showAlert({message: message || 'Operazione completata con successo', type: 'success'});
        setTimeout(() => {
            window.location.reload();
        }, 3000);
    };

    const handleError = (e) => {
        const message = e?.response?.data?.message || e?.message || 'Si è verificato un errore';
        showAlert({message, type: 'error', centered: false});
    };

    const buildSuccessMessage = (data, fallback) => {
        if (!data) return fallback;
        return data.message || fallback || 'Operazione completata con successo';
    };

    const onGenerateFromPoints = async () => {
        try {
            setLoadingPoints(true);
            const data = await generateVoucherFromPoints();
            handleSuccess(buildSuccessMessage(data, 'Voucher generato dai punti con successo'));
        } catch (e) {
            handleError(e);
        } finally {
            setLoadingPoints(false);
        }
    };

    const onGenerateFromCluster = async () => {
        try {
            setLoadingCluster(true);
            const data = await generateVoucherFromCluster();
            handleSuccess(buildSuccessMessage(data, 'Voucher generato dal cluster con successo'));
        } catch (e) {
            handleError(e);
        } finally {
            setLoadingCluster(false);
        }
    };

    if (showErrorMessage) {
        return <ErrorAlert>Si è verificato un errore di caricamento</ErrorAlert>;
    }

    if (!vouchers) {
        return <Spinner/>;
    }

    return (
        <main className="gc-wrap gc-wrap--page">

            {/* Notice "Come funzionano i tuoi voucher" */}
            <div className="gc-notice gc-notice--mb-lg">
                <div className="gc-notice__gwm"/>
                <span className="gc-notice__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                    </svg>
                </span>
                <div className="gc-notice__body">
                    {noticeHtml ? (
                        <div dangerouslySetInnerHTML={{__html: noticeHtml}}/>
                    ) : (
                        <>
                            <p className="gc-notice__title">Come funzionano i tuoi voucher</p>
                            <p className="gc-notice__text">
                                I voucher del Club ti permettono di ottenere sconti e premi sui servizi Giocamondo. Esistono due tipologie:
                            </p>
                            <ol className="gc-notice__list">
                                <li><strong>Punti accumulati</strong> — i voucher generati dai tuoi punti sono utilizzabili quando vuoi, con validità di 10 anni.</li>
                                <li><strong>Cluster Membership</strong> — sconto esclusivo in base al tuo livello, con validità entro il 30 settembre prossimo venturo.</li>
                            </ol>
                            <span className="gc-notice__hl">
                                I punti guadagnati nell'anno in corso saranno spendibili a partire dall'anno successivo.
                            </span>
                            <p className="gc-notice__note gc-notice__note--mb">
                                In fase di prenotazione potrai inserire fino a un massimo di tre codici voucher.
                            </p>
                        </>
                    )}
                    <div className={`gc-notice__actions${noticeHtml ? ' gc-notice__actions--spaced' : ''}`}>
                        <MuiButton
                            variant="contained"
                            color="primary"
                            onClick={onGenerateFromPoints}
                            disabled={loadingPoints || !user.canGeneratePointsVoucher}
                        >
                            {loadingPoints ? 'Generazione…' : 'Genera voucher da punti'}
                        </MuiButton>
                        <MuiButton
                            variant="outlined"
                            onClick={onGenerateFromCluster}
                            disabled={loadingCluster || !user.canGenerateClusterVoucher}
                        >
                            {loadingCluster ? 'Generazione…' : 'Genera voucher da cluster'}
                        </MuiButton>
                    </div>
                </div>
            </div>

            {/* Band sezione */}
            <div className="gc-band">
                <div className="gc-band__title">
                    <h2>I tuoi voucher</h2>
                    <span className="gc-band__rule"/>
                </div>
                <span className="gc-band__count">
                    {vouchers.length} {vouchers.length === 1 ? 'voucher' : 'voucher'}
                </span>
            </div>

            {vouchers.length === 0 ? (
                <EmptyText>Nessun voucher disponibile</EmptyText>
            ) : (
                <div className="MuiPaper-root MuiPaper-elevation1 MuiPaper-rounded gc-table-scroll">
                    <Table className="MuiTable-root tb_voucher" role="table">
                        <TableHead className="MuiTableHead-root">
                            <TableRow className="MuiTableRow-root">
                                <TableCell>Codice voucher</TableCell>
                                <TableCell>Valore voucher</TableCell>
                                <TableCell>Valore consumato</TableCell>
                                <TableCell>Sconto integrativo</TableCell>
                                <TableCell>Sconto consumato</TableCell>
                                <TableCell>Dettaglio</TableCell>
                                <TableCell>Valore totale</TableCell>
                                <TableCell>Scadenza</TableCell>
                                <TableCell>Riscattato</TableCell>
                                <TableCell>Divisione</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="MuiTableBody-root">
                            {vouchers.map((voucher, index) => {
                                const expiration = new Date(voucher.expirationDate);
                                const daysLeft = differenceInDays(expiration, new Date());
                                const expiringSoon = !voucher.redeemed && daysLeft >= 0 && daysLeft <= 30;
                                const totalValue = (voucher.voucherValue + voucher.discountValue) / 100;

                                return (
                                    <TableRow key={index} className="MuiTableRow-root">
                                        <TableCell className="gc-cell--mono">
                                            {voucher.voucherCode}
                                        </TableCell>
                                        <TableCell>€ {(voucher.voucherValue / 100).toFixed(2)}</TableCell>
                                        <TableCell>€ {(voucher.paymentMethodConsumed / 100).toFixed(2)}</TableCell>
                                        <TableCell>€ {(voucher.discountValue / 100).toFixed(2)}</TableCell>
                                        <TableCell>€ {(voucher.valueDiscountConsumed / 100).toFixed(2)}</TableCell>
                                        <TableCell className="gc-cell--muted">
                                            {voucher.voucherDescription}
                                        </TableCell>
                                        <TableCell className="gc-cell--amount">
                                            € {totalValue.toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(expiration, 'dd/MM/yyyy')}
                                            {expiringSoon && (
                                                <span className="gc-pill gc-pill--warning gc-pill--inline">
                                                    In scadenza
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`gc-pill ${voucher.redeemed ? 'gc-pill--success' : 'gc-pill--neutral'}`}>
                                                {voucher.redeemed ? 'Sì' : 'No'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {voucher.division ? (
                                                <span className="gc-sc-badge gc-sc-badge--division">{voucher.division}</span>
                                            ) : (
                                                <span className="gc-text--faint">—</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
        </main>
    );
};

export default Vouchers;
